/* eslint-disable max-lines -- Users list: multi-filter toolbar, pagination, row actions, and status-driven empty state all compose here. */
import {
  DimeDataTable,
  DimeEmpty,
  DimeEmptyDescription,
  DimeEmptyHeader,
  DimeEmptyMedia,
  DimeEmptyTitle,
  toast,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import {
  GET_USERS,
  type GetUsersQueryResult,
  type GetUsersQueryVariables,
  type SysUserListWhere,
} from '../../modules/iam/operations/index.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { makeUsersFilterHandlers } from '../../modules/users/make-users-filter-handlers.js';
import { useUsersFilterOptions } from '../../modules/users/use-users-filter-options.js';
import { UsersFilterChips } from '../../modules/users/users-filter-chips.js';
import {
  buildUsersEmptyMessage,
  buildUsersListWhere,
  type UsersActiveStatus,
} from '../../modules/users/users-list-utils.js';
import { UsersToolbarFilters } from '../../modules/users/users-toolbar-filters.js';
import { makeUserColumns, type UserRow } from './-users-columns.js';

// Verified against SORTABLE_FIELDS.SysUser; `roles` is a computed array, so it is unsortable.
export const USER_SORT_MAP: Record<string, string[]> = {
  firstName: ['firstName'],
  lastName: ['lastName'],
  email: ['email'],
  phone: ['phone'],
  activeFlag: ['activeFlag'],
};

export const Route = createFileRoute('/users/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: UsersPage,
});

const BOTH_ACTIVE_STATUSES: UsersActiveStatus[] = ['active', 'inactive'];

// eslint-disable-next-line react-refresh/only-export-components
function UsersPage() {
  const navigate = useNavigate();
  const [rowSelection, setRowSelection] = useState({});
  // A selected-row export scans only the filtered rows, so a narrower filter must drop hidden selections.
  const clearRowSelection = useCallback(() => setRowSelection({}), []);
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [activeStatuses, setActiveStatuses] = useState<UsersActiveStatus[]>(['active']);

  const { permissions } = usePermissions();
  const canEdit = permissions.includes('user:edit');

  const { brands, roles, states, brandsError, rolesError, statesError } = useUsersFilterOptions();

  const {
    onBrandFilterChange,
    onRoleFilterChange,
    onStateFilterChange,
    onActiveStatusesChange,
    onRemoveBrand,
    onRemoveRole,
    onRemoveState,
    onRemoveActiveStatus,
    onClearAll,
  } = makeUsersFilterHandlers({
    setBrandFilter,
    setRoleFilter,
    setStateFilter,
    setActiveStatuses,
    reset: clearRowSelection,
  });

  const where = buildUsersListWhere({
    brandIds: brandFilter,
    roleIds: roleFilter,
    stateIds: stateFilter,
    activeStatuses,
  });

  const {
    rows: users,
    loading,
    error,
    refetch,
    search,
    tableProps,
  } = useListTable<
    GetUsersQueryResult,
    GetUsersQueryVariables,
    UserRow,
    SysUserListWhere | undefined
  >({
    query: GET_USERS,
    extractConnection: (data) => data?.sysUsers,
    defaultSort: [{ id: 'lastName', desc: false }],
    sortMap: USER_SORT_MAP,
    sortTiebreakerField: null, // SysUserOrderBy has no `id` field yet.
    where,
  });

  const listTableProps = {
    ...tableProps,
    onSearchChange: (value: string) => {
      clearRowSelection();
      tableProps.onSearchChange(value);
    },
  };

  const columns = useMemo(
    () =>
      makeUserColumns(navigate, {
        canEdit,
        // Refetch in place: the cursor stays valid after a status change, so the page is kept.
        onChanged: () => {
          void refetch().catch(() => {
            toast.error('User list may be out of date — refresh the page to see the latest.');
          });
        },
      }),
    [navigate, canEdit, refetch],
  );

  // WHY: the default view is Active-only, so that selection is not itself a "filter applied".
  const isDefaultStatusSelection = activeStatuses.length === 1 && activeStatuses[0] === 'active';
  const hasActiveFilters =
    brandFilter.length > 0 ||
    roleFilter.length > 0 ||
    stateFilter.length > 0 ||
    !isDefaultStatusSelection;
  // buildUsersListWhere drops the activeFlag clause for an empty selection, so the list really is
  // showing both statuses -- render both chips rather than a chip-less row with no explanation.
  const chipStatuses = activeStatuses.length === 0 ? BOTH_ACTIVE_STATUSES : activeStatuses;
  const onRemoveChipStatus = (status: UsersActiveStatus) => {
    if (activeStatuses.length === 0) {
      onActiveStatusesChange(BOTH_ACTIVE_STATUSES.filter((value) => value !== status));
      return;
    }
    onRemoveActiveStatus(status);
  };
  const noResultsMessage = buildUsersEmptyMessage(search, hasActiveFilters);
  // Only show the "no users yet" empty state when all status filters are cleared: the default
  // active-only selection is still an effective filter, and a tenant with only inactive users
  // would be stuck with no way to change the status if the toolbar were hidden.
  const showEmptyState =
    !loading &&
    users.length === 0 &&
    !search &&
    !brandFilter.length &&
    !roleFilter.length &&
    !stateFilter.length &&
    activeStatuses.length === 0;

  const listFilters = (
    <UsersToolbarFilters
      brands={brands}
      brandFilter={brandFilter}
      onBrandFilterChange={onBrandFilterChange}
      roles={roles}
      roleFilter={roleFilter}
      onRoleFilterChange={onRoleFilterChange}
      states={states}
      stateFilter={stateFilter}
      onStateFilterChange={onStateFilterChange}
      activeStatuses={activeStatuses}
      onActiveStatusesChange={onActiveStatusesChange}
    />
  );

  const listFilterChips = hasActiveFilters ? (
    <UsersFilterChips
      brands={brands}
      brandFilter={brandFilter}
      onRemoveBrand={onRemoveBrand}
      roles={roles}
      roleFilter={roleFilter}
      onRemoveRole={onRemoveRole}
      states={states}
      stateFilter={stateFilter}
      onRemoveState={onRemoveState}
      activeStatuses={chipStatuses}
      onRemoveActiveStatus={onRemoveChipStatus}
      onClearAll={onClearAll}
    />
  ) : undefined;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1>Users</h1>
          <p className="text-sm text-muted-foreground">
            Click on a user to manage their account and permissions.
          </p>
        </div>
      </div>
      {(brandsError ?? rolesError ?? statesError) && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          Some filters failed to load. Refresh the page to try again.
        </p>
      )}
      {error && users.length === 0 ? (
        <ListErrorState
          title="Could not load users"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : showEmptyState ? (
        <DimeEmpty className="border border-dashed">
          <DimeEmptyHeader>
            <DimeEmptyMedia variant="icon">
              <Users />
            </DimeEmptyMedia>
            <DimeEmptyTitle>No users yet</DimeEmptyTitle>
            <DimeEmptyDescription>
              Users are added automatically the first time they sign in with Google.
            </DimeEmptyDescription>
          </DimeEmptyHeader>
        </DimeEmpty>
      ) : (
        <DimeDataTable<UserRow, string>
          columns={columns}
          data={users}
          maxBodyHeight="viewport"
          onRowClick={(user) => {
            if (user.id)
              navigate({
                to: '/users/$userId',
                params: { userId: user.id },
                search: { edit: false },
              });
          }}
          searchColumn="lastName"
          searchPlaceholder="Search users..."
          showViewOptions
          showSelect
          selected={rowSelection}
          onUpdateSelected={setRowSelection}
          filters={listFilters}
          filterChips={listFilterChips}
          emptyMessage={noResultsMessage}
          loadingRowCount={tableProps.pageSize}
          exportFileName="users"
          {...listTableProps}
        />
      )}
    </div>
  );
}
